import { styled } from "@mui/material/styles";

import HomeIcon from "@mui/icons-material/Home";
import WarningIcon from "@mui/icons-material/ReportProblemRounded";
import { Typography, Grid } from "@mui/material";
import { FormattedMessage } from "react-intl";

import { config } from "app.config";
import Logo from "components/Shared/Logo";
import { fixContrast } from "foundations/utils/styleHelpers";
import { RouterMuiButton } from "foundations/RouterLink/RouterMuiButton";

const PREFIX = "DefaultErrorFallback";

const classes = {
  gridContainer: `${PREFIX}-gridContainer`,
  warningIconContainer: `${PREFIX}-warningIconContainer`,
  message: `${PREFIX}-message`,
  homeButton: `${PREFIX}-homeButton`,
  logoContainer: `${PREFIX}-logoContainer`,
  stackContainer: `${PREFIX}-stackContainer`,
  stackText: `${PREFIX}-stackText`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.gridContainer}`]: {
    minHeight: "100vh",
    textAlign: "center",
  },

  [`& .${classes.warningIconContainer}`]: {
    fontSize: 224,
  },

  [`& .${classes.message}`]: {
    marginTop: 36,
    marginBottom: 64,
    color: fixContrast(theme.palette.warning.dark, "#FFFFFF"),
    fontWeight: "bold",
  },

  [`& .${classes.homeButton}`]: {
    fontWeight: "bold",
    marginBottom: 48,
  },

  [`& .${classes.logoContainer}`]: {
    marginBottom: 24,
  },

  [`& .${classes.stackContainer}`]: {
    borderWidth: 1,
    borderColor: "#BBBBBB",
    borderStyle: "solid",
    borderRadius: 5,
    paddingLeft: 16,
    paddingRight: 16,
  },

  [`& .${classes.stackText}`]: {
    color: "#333333",
  },
}));

interface DefaultErrorFallbackProps {
  // Router error components give an unknown value, because any value is throwable.
  error: unknown;
}

export function DefaultErrorFallback(props: DefaultErrorFallbackProps) {
  const error =
    props.error instanceof Error ? props.error : new Error(String(props.error));

  return (
    <StyledGrid
      container
      sx={{
        alignItems: "center",
        justifyContent: "center",
      }}
      className={classes.gridContainer}
      wrap="nowrap"
    >
      <Grid
        size={{
          xs: 12,
          sm: 10,
          md: 8,
        }}
      >
        <div className={classes.warningIconContainer}>
          <WarningIcon color="warning" fontSize="inherit" />
        </div>

        <Grid
          container
          spacing={1}
          wrap="nowrap"
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Grid>
            <Typography
              variant="h6"
              component="h1"
              color="blue"
              className={classes.message}
            >
              <FormattedMessage
                defaultMessage="Something went wrong here.{br}Our engineering team has been notified."
                description="Message that appears when the app experiences an error"
                values={{ br: <br /> }}
              />
            </Typography>
          </Grid>
        </Grid>

        <RouterMuiButton
          to="/"
          reloadDocument
          variant="contained"
          color="primary"
          size="large"
          startIcon={<HomeIcon />}
          className={classes.homeButton}
        >
          <FormattedMessage
            defaultMessage="Return To Home"
            description="Button label for link that redirects to home page"
          />
        </RouterMuiButton>

        <div className={classes.logoContainer}>
          <Logo coloredDefault isSessionLogo maxHeight="45px" />
        </div>

        {config.environment === "development" && (
          <div className={classes.stackContainer}>
            <p className={classes.stackText}>
              {error.message}
              {error.stack &&
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                ` | ${
                  error.stack.split("\n")[
                    navigator.userAgent.includes("Chrome") ? 1 : 0
                  ]
                }`}
            </p>
          </div>
        )}
      </Grid>
    </StyledGrid>
  );
}
